import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { ESubscriptionCancellationReason, ESubscriptionStatus } from "@devowl-wp/api-real-commerce";
import { SubscriptionCard } from "./subscriptionCard.js";
import { RealCommerceError } from "../../../../../exceptions/realCommerceError.js";
import { useStores } from "../../../../../store/stores.js";
import { __ } from "../../../../../utils/i18n.js";
import { disableFomoCoupon } from "../../../../fomoCoupon/banner.js";
import { useAccountV2ErrorHandling } from "../../../hooks/useAccountV2ErrorHandling.js";
import { AccountSkeleton } from "../../accountSkeleton.js";
import { EmptyState } from "../../emptyState.js";
import { EnvatoActivationModal } from "../../modals/envatoActivationModal.js";
// Stable singletons so `?? EMPTY_*` fallbacks keep downstream memo caches valid.
const EMPTY_PRODUCT_IDS = [];
const EMPTY_SUBSCRIPTIONS = [];
const SubscriptionList = observer(()=>{
    const { accountV2Store } = useStores();
    const { session } = accountV2Store;
    const handleRealCommerceError = useAccountV2ErrorHandling();
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isEnvatoModalOpen, setIsEnvatoModalOpen] = useState(false);
    const subscriptionProductIds = session?.subscriptionProductIds ?? EMPTY_PRODUCT_IDS;
    const activeSubscriptions = session?.activeSubscriptions ?? EMPTY_SUBSCRIPTIONS;
    const subscriptionsSize = session?.subscriptions.size ?? 0;
    const firstNonCancelledIndex = useMemo(()=>activeSubscriptions.findIndex(({ data: { status } })=>status !== ESubscriptionStatus.Canceled), [
        activeSubscriptions
    ]);
    useEffect(()=>{
        accountV2Store.fetchSubscriptionsAndLicenses().then(()=>setHasLoaded(true)).catch((e)=>{
            if (e instanceof RealCommerceError) {
                // Bootstrap fetch failure escalates to the full-page
                // FatalError view instead of an overlay on an empty list.
                handleRealCommerceError(e, {
                    onBackendDown: (error)=>accountV2Store.setFatalError(error)
                });
            }
        });
    }, []);
    useEffect(()=>{
        for (const productId of subscriptionProductIds){
            disableFomoCoupon({
                reason: "user-of-product",
                productId
            });
        }
    }, [
        subscriptionProductIds
    ]);
    if (!hasLoaded) {
        return /*#__PURE__*/ _jsx(AccountSkeleton, {});
    }
    if (subscriptionsSize === 0) {
        return /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-card-outline",
            children: /*#__PURE__*/ _jsx(EmptyState, {
                heading: __("No purchases or subscriptions yet")
            })
        });
    }
    // Default-expand a card whenever the customer needs immediate attention
    // on it (payment-failed → restore CTA, past-due → update payment).
    // Otherwise expand only the topmost non-cancelled card so the customer
    // always lands on something actionable.
    const isDefaultExpanded = (subscription, index)=>{
        const isPaymentFailedExpired = subscription.isExpired && subscription.data.cancellationReason === ESubscriptionCancellationReason.PaymentFailed;
        return isPaymentFailedExpired || subscription.isPastDue || index === firstNonCancelledIndex;
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "rc-account-v2-subscription-list",
        children: [
            activeSubscriptions.map((subscription, index)=>/*#__PURE__*/ _jsx(SubscriptionCard, {
                    subscription: subscription,
                    defaultExpanded: isDefaultExpanded(subscription, index)
                }, subscription.data.id)),
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                className: "rc-account-v2-subscription-list__envato-card",
                onClick: ()=>setIsEnvatoModalOpen(true),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-subscription-list__envato-text",
                        children: __("Purchased one of our plugins on codecanyon.net?")
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: "rc-account-v2-subscription-list__envato-link",
                        children: __("Activate your purchase.")
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(EnvatoActivationModal, {
                open: isEnvatoModalOpen,
                onClose: ()=>setIsEnvatoModalOpen(false)
            })
        ]
    });
});
export { SubscriptionList };
